import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const WITHIN_COLOR = '#2563eb';
const BETWEEN_COLOR = '#f59e0b';

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    marginBottom: '16px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    margin: 0,
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    margin: '4px 0 0 0',
  },
  chartRow: {
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap',
  },
  chartSection: {
    flex: '1 1 300px',
    minWidth: '300px',
  },
  sectionTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '8px',
  },
  container: {
    overflowX: 'auto',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    marginBottom: '12px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    fontWeight: 500,
  },
  legendSwatch: {
    width: '14px',
    height: '14px',
    borderRadius: '2px',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

export default function PriceVarianceDecomposition({ data }) {
  const stackedSvgRef = useRef(null);
  const groupedSvgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const components = useMemo(() => {
    if (!data || !data.components) return [];
    return data.components;
  }, [data]);

  const byField = useMemo(() => {
    if (!data || !data.byField) return [];
    return data.byField;
  }, [data]);

  // Stacked horizontal bar
  useEffect(() => {
    if (components.length === 0) return;

    const svg = d3.select(stackedSvgRef.current);
    svg.selectAll('*').remove();

    const width = 700;
    const height = 120;
    const margin = { top: 30, right: 20, bottom: 30, left: 20 };
    const innerW = width - margin.left - margin.right;
    const barHeight = 36;

    svg.attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .style('width', '100%')
      .style('height', 'auto');

    const g = svg.append('g').attr('transform', `translate(${margin.left}, ${margin.top})`);

    // Total variance annotation
    if (data.totalVariance != null) {
      g.append('text')
        .attr('x', 0)
        .attr('y', -12)
        .attr('font-size', '11px')
        .attr('fill', '#6b7280')
        .text(`Total Variance: ${data.totalVariance.toFixed(2)}`);
    }

    // x scale: 0 to 100%
    const x = d3.scaleLinear().domain([0, 100]).range([0, innerW]);

    // Build cumulative offsets
    let cumulative = 0;
    const segments = components.map((c) => {
      const seg = {
        source: c.source,
        variance: c.variance,
        percentage: c.percentage,
        color: c.color,
        x0: cumulative,
        x1: cumulative + c.percentage,
      };
      cumulative += c.percentage;
      return seg;
    });

    const barY = 0;

    // Draw segments
    segments.forEach((seg) => {
      const segX = x(seg.x0);
      const segW = x(seg.x1) - x(seg.x0);

      g.append('rect')
        .attr('x', segX)
        .attr('y', barY)
        .attr('width', Math.max(0, segW))
        .attr('height', barHeight)
        .attr('fill', seg.color)
        .attr('rx', seg.x0 === 0 ? 4 : 0)
        .attr('ry', seg.x0 === 0 ? 4 : 0)
        .style('cursor', 'pointer')
        .on('mousemove', (event) => {
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            source: seg.source,
            variance: seg.variance,
            percentage: seg.percentage,
          });
        })
        .on('mouseleave', () => {
          setTooltip(null);
        });

      // Round right corners on last segment
      if (seg.x1 >= cumulative - 0.01) {
        g.append('rect')
          .attr('x', segX)
          .attr('y', barY)
          .attr('width', Math.max(0, segW))
          .attr('height', barHeight)
          .attr('fill', seg.color)
          .attr('rx', 4)
          .attr('ry', 4)
          .style('pointer-events', 'none');
        // Re-cover left side if not first
        if (seg.x0 > 0) {
          g.append('rect')
            .attr('x', segX)
            .attr('y', barY)
            .attr('width', Math.min(8, Math.max(0, segW)))
            .attr('height', barHeight)
            .attr('fill', seg.color)
            .style('pointer-events', 'none');
        }
      }

      // Percentage label inside or outside segment
      const labelText = `${seg.percentage.toFixed(1)}%`;
      const textWidth = labelText.length * 6.5;
      if (segW > textWidth + 8) {
        g.append('text')
          .attr('x', segX + segW / 2)
          .attr('y', barY + barHeight / 2)
          .attr('dy', '0.35em')
          .attr('text-anchor', 'middle')
          .attr('font-size', '11px')
          .attr('font-weight', 600)
          .attr('fill', '#fff')
          .style('pointer-events', 'none')
          .text(labelText);
      } else if (segW > 4) {
        // Place label below
        g.append('text')
          .attr('x', segX + segW / 2)
          .attr('y', barY + barHeight + 14)
          .attr('text-anchor', 'middle')
          .attr('font-size', '9px')
          .attr('font-weight', 600)
          .attr('fill', seg.color)
          .style('pointer-events', 'none')
          .text(labelText);
      }
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [components, data]);

  // Grouped bar chart for byField
  useEffect(() => {
    if (byField.length === 0) return;

    const svg = d3.select(groupedSvgRef.current);
    svg.selectAll('*').remove();

    const width = 700;
    const height = 260;
    const margin = { top: 20, right: 20, bottom: 40, left: 60 };
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;

    svg.attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .style('width', '100%')
      .style('height', 'auto');

    const g = svg.append('g').attr('transform', `translate(${margin.left}, ${margin.top})`);

    const fields = byField.map((d) => d.field);
    const subgroups = ['withinVariance', 'betweenVariance'];

    const x0 = d3.scaleBand()
      .domain(fields)
      .range([0, innerW])
      .paddingInner(0.3)
      .paddingOuter(0.15);

    const x1 = d3.scaleBand()
      .domain(subgroups)
      .range([0, x0.bandwidth()])
      .padding(0.1);

    let yMax = 0;
    byField.forEach((d) => {
      if (d.withinVariance > yMax) yMax = d.withinVariance;
      if (d.betweenVariance > yMax) yMax = d.betweenVariance;
    });
    yMax = yMax || 1;
    const yPadding = yMax * 0.15;

    const y = d3.scaleLinear()
      .domain([0, yMax + yPadding])
      .nice()
      .range([innerH, 0]);

    const colorMap = {
      withinVariance: WITHIN_COLOR,
      betweenVariance: BETWEEN_COLOR,
    };

    // Grid lines
    g.selectAll('.grid-h')
      .data(y.ticks())
      .enter()
      .append('line')
      .attr('class', 'grid-h')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(d3.axisBottom(x0).tickSize(0))
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '11px')
      .attr('fill', '#6b7280')
      .attr('dy', '0.8em');

    // Y axis
    g.append('g')
      .call(d3.axisLeft(y).ticks(6).tickSize(0).tickFormat((d) => d.toFixed(1)))
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // Y axis label
    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerH / 2)
      .attr('y', -45)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('fill', '#9ca3af')
      .text('Variance');

    // Draw bars
    byField.forEach((fieldData) => {
      const groupX = x0(fieldData.field);

      subgroups.forEach((sub) => {
        const val = fieldData[sub];
        if (val == null || val <= 0) return;

        const barX = groupX + x1(sub);
        const barW = x1.bandwidth();
        const barY = y(val);
        const barH = innerH - barY;

        g.append('rect')
          .attr('x', barX)
          .attr('y', barY)
          .attr('width', barW)
          .attr('height', Math.max(0, barH))
          .attr('fill', colorMap[sub])
          .attr('rx', 2)
          .style('cursor', 'pointer')
          .on('mousemove', (event) => {
            const total = (fieldData.withinVariance || 0) + (fieldData.betweenVariance || 0);
            const pct = total > 0 ? (val / total) * 100 : 0;
            setTooltip({
              x: event.clientX + 12,
              y: event.clientY - 10,
              source: sub === 'withinVariance' ? 'Within State' : 'Between States',
              variance: val,
              percentage: pct,
              field: fieldData.field,
            });
          })
          .on('mouseleave', () => {
            setTooltip(null);
          });

        // Value label on bar
        if (barH > 18) {
          g.append('text')
            .attr('x', barX + barW / 2)
            .attr('y', barY + 14)
            .attr('text-anchor', 'middle')
            .attr('font-size', '10px')
            .attr('font-weight', 600)
            .attr('fill', '#fff')
            .style('pointer-events', 'none')
            .text(val.toFixed(1));
        } else {
          g.append('text')
            .attr('x', barX + barW / 2)
            .attr('y', barY - 4)
            .attr('text-anchor', 'middle')
            .attr('font-size', '10px')
            .attr('font-weight', 600)
            .attr('fill', colorMap[sub])
            .style('pointer-events', 'none')
            .text(val.toFixed(1));
        }
      });
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [byField]);

  if (!data || (!data.components && !data.byField)) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.title}>Price Variance Decomposition</div>
          <div style={styles.subtitle}>Breaking down total price variation by source</div>
        </div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>Price Variance Decomposition</div>
        <div style={styles.subtitle}>Breaking down total price variation by source</div>
      </div>
      <div style={styles.chartRow}>
        {/* Left: Stacked horizontal bar */}
        <div style={styles.chartSection}>
          <div style={styles.sectionTitle}>Variance by Source</div>
          <div style={styles.legend}>
            {components.map((c) => (
              <div key={c.source} style={styles.legendItem}>
                <span style={{ ...styles.legendSwatch, background: c.color }} />
                <span style={{ color: '#374151' }}>{c.source}</span>
              </div>
            ))}
          </div>
          <div style={styles.container}>
            <svg ref={stackedSvgRef} />
          </div>
        </div>
        {/* Right: Grouped bar chart */}
        <div style={styles.chartSection}>
          <div style={styles.sectionTitle}>Within vs Between Variance by Field</div>
          <div style={styles.legend}>
            <div style={styles.legendItem}>
              <span style={{ ...styles.legendSwatch, background: WITHIN_COLOR }} />
              <span style={{ color: '#374151' }}>Within State</span>
            </div>
            <div style={styles.legendItem}>
              <span style={{ ...styles.legendSwatch, background: BETWEEN_COLOR }} />
              <span style={{ color: '#374151' }}>Between States</span>
            </div>
          </div>
          <div style={styles.container}>
            <svg ref={groupedSvgRef} />
          </div>
        </div>
      </div>
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {tooltip.source}
          </div>
          {tooltip.field && (
            <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px' }}>
              {tooltip.field}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Variance</span>
            <span style={{ fontWeight: 600 }}>{tooltip.variance.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
            <span style={{ color: '#6b7280' }}>% of Total</span>
            <span style={{ fontWeight: 600 }}>{tooltip.percentage.toFixed(1)}%</span>
          </div>
        </div>
      )}
    </div>
  );
}
